import { PricingLineItem } from '@cutr/constants/cutlist';
import { ALL_CUSTOM_ARTICLES } from '@cutr/constants/material';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { getMaterialById } from '@/api/materials';
import { PriceData } from '@/api/pricing';
import { useCutlistState } from '@/api/store';
import i18n from '@/i18n';
import { useCurrentFeatures } from '@/theme';
import { currencyFormatter } from '@/utils/format';

import styles from './Price.module.css';

const getPrice = (
  totalAmount: number | null | undefined,
  vatRate: number,
  vatDisplayMode: string,
  currency: string = 'EUR'
) => {
  if (!totalAmount) return null;

  if (vatDisplayMode === 'inclusive') {
    totalAmount *= (100 + vatRate) / 100;
  }
  return currencyFormatter(totalAmount / 100, currency);
};

const getUnit = (unit: string, quantity: number) => {
  if (unit === 'm') return `${quantity}m`;
  if (unit === 'piece')
    return i18n.t('cutlist-summary.price.piece', { count: quantity });
  return '';
};

type PricingLineItemsProps = {
  priceData: PriceData[];
  currency: string;
  vatRate: number;
  vatDisplayMode: string;
};

export const PricingLineItems = ({
  priceData,
  currency,
  vatRate,
  vatDisplayMode,
}: PricingLineItemsProps) => {
  const { pricingGranularity } = useCurrentFeatures();

  if (pricingGranularity === 'detailed') {
    return (
      <DetailedPricingLineItems
        priceData={priceData}
        currency={currency}
        vatRate={vatRate}
        vatDisplayMode={vatDisplayMode}
      />
    );
  }

  if (pricingGranularity === 'aggregated') {
    return (
      <AggregatedPricingLineItems
        priceData={priceData}
        currency={currency}
        vatRate={vatRate}
        vatDisplayMode={vatDisplayMode}
      />
    );
  }

  return null;
};

const AggregatedPricingLineItems = ({
  priceData,
  currency,
  vatRate,
  vatDisplayMode,
}: {
  priceData: PriceData[];
  currency: string;
  vatRate: number;
  vatDisplayMode: string;
}) => {
  return (
    <ul className={styles.cardPrice}>
      {priceData.map((item, i) => {
        return (
          <React.Fragment key={i}>
            {!['surcharges', 'material'].includes(item.category) && (
              <li className="flexAlign opposites">
                <span>{item.label}</span>
                <span>
                  {Boolean(item.cost) &&
                    getPrice(item.cost, vatRate, vatDisplayMode, currency)}
                </span>
              </li>
            )}

            {['surcharges'].includes(item.category) &&
              item.items?.map((subi, i) => (
                <li key={i} className="flexAlign opposites">
                  <span>{subi.description}</span>
                  <span>
                    {Boolean(subi.totalAmount) &&
                      getPrice(
                        subi.totalAmount,
                        vatRate,
                        vatDisplayMode,
                        currency
                      )}
                  </span>
                </li>
              ))}

            {['material'].includes(item.category) &&
              item.items?.map((subi, i) => (
                <MaterialPricingLineItem
                  key={i}
                  item={subi}
                  currency={currency}
                />
              ))}
          </React.Fragment>
        );
      })}
    </ul>
  );
};

const MATERIAL_CATEGORIES = ['material', 'beam'];

const DetailedPricingLineItems = ({
  priceData,
  currency,
  vatRate,
  vatDisplayMode,
}: {
  priceData: PriceData[];
  currency: string;
  vatRate: number;
  vatDisplayMode: string;
}) => {
  const pli = priceData
    .flatMap((item) => item.items)
    .sort((a, b) => a.index - b.index);

  return (
    <ul className={styles.cardPrice}>
      {pli.map((item, i) => {
        return (
          <React.Fragment key={i}>
            {!MATERIAL_CATEGORIES.includes(item.category) && (
              <li className="flexAlign opposites">
                <span>{item.description}</span>
                <span>
                  {Boolean(item.totalAmount) &&
                    getPrice(
                      item.totalAmount,
                      vatRate,
                      vatDisplayMode,
                      currency
                    )}
                </span>
              </li>
            )}

            {MATERIAL_CATEGORIES.includes(item.category) && (
              <MaterialPricingLineItem
                key={i}
                item={item}
                currency={currency}
              />
            )}
          </React.Fragment>
        );
      })}
    </ul>
  );
};

const MaterialPricingLineItem = ({
  item,
  currency,
}: {
  item: PricingLineItem;
  currency: string;
}) => {
  const { t } = useTranslation();
  const { vatRate } = useCutlistState();
  const { vatDisplayMode } = useCurrentFeatures();

  const price = getPrice(item.totalAmount, vatRate, vatDisplayMode, currency);
  const material = getMaterialById(item.materialId);

  const { thicknessUM } = material || {};
  const lengthMM = item.meta?.stockLengthMM ?? material?.lengthMM;
  const widthMM = item.meta?.stockWidthMM ?? material?.widthMM;
  const thickness = (thicknessUM || 0) / 1000;

  const isCustomArticle =
    material &&
    (ALL_CUSTOM_ARTICLES as (typeof material)['articleCode'][]).includes(
      material.articleCode
    );

  const ebSize = isCustomArticle && material.shortDescription;
  const sizes = [lengthMM, widthMM, thickness].filter(Boolean);

  const itemDimensions =
    !isCustomArticle && Boolean(sizes.length)
      ? `${sizes.join('x')} mm`
      : undefined;

  return (
    <li>
      <div className="flexAlign opposites">
        <span>{item.description}</span>
        <span>{price ? price : <>&mdash;</>}</span>
      </div>
      <span>
        {getUnit(item.unit, item.quantity)}
        {(itemDimensions || ebSize) && ', '}
        {itemDimensions || ebSize}
        {!price && <span>&nbsp;</span>}
        {!price && (
          <span className={styles.manuallyQuoted}>
            {t('cutlist-summary.manualQuote')}
          </span>
        )}
      </span>
    </li>
  );
};
